import { EpisodeState } from "@/api/episodes-types";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { EpisodeBreadcrumbs } from "@/components/ui/breadcrumbs/EpisodeBreadcrumbs";
import { TimestampBreadcrumbs } from "@/components/ui/breadcrumbs/TimestampBreadcrumbs";
import { BoxLoader } from "@/components/ui/loaders";
import { EpisodeStateChip } from "@/components/ui/state-chips/EpisodeStateChip";
import { TagsRow } from "@/components/ui/tags/TagsRow";
import { TextBox } from "@/components/ui/TextBox";
import { useEpisodeById } from "@/queries/episodes/use-episode-by-id";
import { Box, Button, darken, List, Stack, Typography } from "@mui/material";
import { green, red } from "@mui/material/colors";

type Props = {
  id: string;
  onRejectWholeEpisode?: () => void;
};

export function EpisodeDetails({ id, onRejectWholeEpisode }: Props) {
  const { data, error, isLoading } = useEpisodeById(id);

  function renderData() {
    if (!data) {
      return null;
    }

    const { modelName, pages, textRejectionReasons } = data;

    return (
      <>
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "33px", // height of Button, set in case no buttons are displayed to have consistent height across different cases
          }}
        >
          <EpisodeBreadcrumbs {...data} />
          {onRejectWholeEpisode && (
            <Button
              variant="outlined"
              color="error"
              onClick={onRejectWholeEpisode}
              sx={{ height: 32 }}
            >
              {"Reject whole story"}
            </Button>
          )}
        </Stack>
        <TimestampBreadcrumbs {...data} />
        <Typography color={"textSecondary"}>
          {`Model name: ${modelName || "Missing model name!"}`}
        </Typography>
        <Typography color={"textSecondary"}>
          {`Assignee: ${data.visualsAssignee?.username ?? "Unassigned"}`}
        </Typography>
        <Box width={130}>
          <EpisodeStateChip size={"medium"} state={data.state} />
        </Box>
        {!!textRejectionReasons?.length && (
          <Box>
            <Typography variant={"body1"} color={"textSecondary"}>
              Rejection reasons:
            </Typography>
            <List sx={{ listStyle: "initial", pl: 2, pb: 0 }}>
              {textRejectionReasons.map((reason) => (
                <li key={reason}>
                  <Typography color={"textSecondary"}>{reason}</Typography>
                </li>
              ))}
            </List>
          </Box>
        )}
        <TagsRow episodeId={id} />
        <TextBox
          sx={[
            {
              backgroundColor:
                data.state === EpisodeState.rejected
                  ? red[50]
                  : [EpisodeState.accepted, EpisodeState.pending].includes(
                        data.state,
                      )
                    ? green[50]
                    : undefined,
            },
            (theme) =>
              theme.applyStyles("dark", {
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor:
                  data.state === EpisodeState.rejected
                    ? darken(red[900], 0.2)
                    : [EpisodeState.accepted, EpisodeState.pending].includes(
                          data.state,
                        )
                      ? darken(green[900], 0.2)
                      : "background.paper",
              }),
          ]}
        >
          {pages.length > 0 ? (
            pages
              .toSorted((a, b) => a.pageNumber - b.pageNumber)
              .map((page, idx) => (
                <Box key={page.id} sx={{ pb: 2 }}>
                  <Typography
                    variant="body2"
                    color={"textSecondary"}
                  >{`Page ${page.pageNumber}`}</Typography>
                  <Typography
                    component={idx ? `p` : `h1`}
                    variant={idx ? "body1" : "h5"}
                    key={idx}
                  >
                    {page.text}
                  </Typography>
                </Box>
              ))
          ) : (
            <Typography component={"p"}>{"Missing story text!"}</Typography>
          )}
        </TextBox>
      </>
    );
  }

  return (
    <Stack gap={1.5}>
      <ApiErrorAlert error={error} />
      <BoxLoader isLoading={isLoading} height={100} />
      {renderData()}
    </Stack>
  );
}
